import { Box, LinearProgress, Typography } from '@mui/material';
import { IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { IPersonalisedJourneyItem } from '../dummyData/home';
import { useNavigate } from 'react-router-dom';

function formatDaysAgo(isoDate: string): string {
  const start = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return days <= 1 ? `Started ${days} Day ago` : `Started ${days} Days ago`;
}

function formatTimeSpent(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;

  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;

  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  const parts: string[] = [];

  if (days > 0) {
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    return parts.join(' ');
  }

  if (hours > 0) {
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  if (minutes > 0) {
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  }

  return `${seconds}s`;
}

function getMasteryColor(percent: number): string {
  if (percent < 40) return '#FF5252';
  if (percent < 70) return '#007CDC';
  return '#4CAF50';
}

const styles: IStyles = {
  card: {
    width: '100%',
    minWidth: 0,
    borderRadius: { xs: pxToRem(12), md: pxTovW(12) },
    bgcolor: 'common.white',
    boxShadow: '0px 2px 12px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
  },
  bodyRow: {
    display: 'flex',
    gap: '10px',
    p: { xs: pxToRem(10), md: pxTovW(10) },
    alignItems: 'stretch',
  },
  labelsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: { xs: pxToRem(8), md: pxTovW(8) },
  },
  pill: {
    bgcolor: '#4153B9',
    color: 'white',
    px: { xs: pxToRem(10), md: pxTovW(12) },
    py: { xs: pxToRem(4), md: pxTovW(4) },
    borderRadius: 50,
    fontSize: { xs: pxToRem(12), md: pxToRem(12) },
    fontWeight: 600,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  pillNextAction: {
    color: 'text.primary',
    px: { xs: pxToRem(8), md: pxTovW(8) },
    py: { xs: pxToRem(4), md: pxTovW(4) },
    fontSize: { xs: pxToRem(10), md: pxToRem(10) },
    fontWeight: 600,
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  dot: {
    borderRadius: '50%',
    bgcolor: '#007CDC',
    width: 8,
    height: 8,
    marginRight: 1,
  },
  dotText: {
    fontSize: {
      xs: pxToRem(12),
      md: pxToRem(12),
    },
  },
  imageWrap: {
    width: '30%',
    aspectRatio: '1 / 1',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
  },
  poster: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
    minWidth: 0,
  },
  classSubject: {
    color: '#007CDC',
    fontSize: { xs: pxToRem(12), md: pxToRem(12) },
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  chapterName: {
    fontWeight: 700,
    color: 'text.primary',
    fontSize: { xs: pxToRem(14), md: pxToRem(14) },
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.5,
  },
  metaText: {
    color: 'text.secondary',
    fontSize: { xs: pxToRem(11), md: pxToRem(11) },
  },
  selfLearning: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'text.secondary',
    fontSize: { xs: pxToRem(11), md: pxToRem(11) },
    fontWeight: 700,
    textTransform: 'capitalize',
  },
  masteryLabel: {
    color: 'text.primary',
    fontSize: { xs: pxToRem(12), md: pxToRem(12) },
    fontWeight: 500,
  },
  masteryHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1,
  },
  progressWrap: {
    width: '100%',
    mt: 0.5,
  },
  progressBar: {
    width: '100%',
    height: { xs: pxToRem(8), md: pxTovW(8) },
    borderRadius: 50,
    bgcolor: '#E0E0E0',
    '& .MuiLinearProgress-bar': {
      borderRadius: 50,
    },
  },
  percentText: {
    fontSize: { xs: pxToRem(12), md: pxToRem(12) },
    fontWeight: 600,
    minWidth: pxToRem(32),
  },
};

interface IPersonalisedJourneyCardProps {
  item: IPersonalisedJourneyItem;
  onClick?: () => void;
}

export const PersonalisedJourneyCard = ({
  item,
  onClick,
}: IPersonalisedJourneyCardProps) => {
  const masteryColor = getMasteryColor(item.masteryPercent);
  const navigate = useNavigate();

  return (
    <Box sx={styles.card} onClick={onClick}>
      <Box sx={styles.bodyRow}>
        <Box sx={styles.imageWrap}>
          <img
            src={item.posterImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.metaRow}>
            <Typography sx={styles.classSubject}>
              Class {item.class} {item.subject}
            </Typography>
            <Box sx={styles.selfLearning}>
              <Box
                sx={{
                  ...styles.dot,
                  bgcolor: '#3F4D8F',
                }}
              />
              <Typography sx={styles.dotText} component="span">
                {item.mode}
              </Typography>
            </Box>
          </Box>
          <Typography sx={styles.chapterName}>
            {item.chapterName}
            {/* <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> */}
          </Typography>
          <Box
            sx={{
              ...styles.metaRow,
              mt: 'auto',
            }}
          >
            <Typography sx={styles.metaText}>
              {formatDaysAgo(item.startDate)}
            </Typography>
            <Typography sx={styles.metaText}>
              Total Time Spent : {formatTimeSpent(item.timeSpentInSeconds)}
            </Typography>
          </Box>
          <Box
            sx={{
              background: '#F6F5FF',
              borderRadius: { xs: pxToRem(12), md: pxTovW(12) },
              padding: '7px 9px',
              marginTop: 'auto',
            }}
          >
            <Box sx={styles.masteryHeaderRow}>
              <Typography sx={styles.masteryLabel}>Your Mastery</Typography>
              <Typography sx={{ ...styles.percentText, color: masteryColor }}>
                {item.masteryPercent}%
              </Typography>
            </Box>
            <Box sx={styles.progressWrap}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, item.masteryPercent)}
                sx={{
                  ...styles.progressBar,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: masteryColor,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalisedJourneyCard;
